import { Link } from 'react-router-dom';
import { 
  Target, 
  Users, 
  Shield, 
  ArrowRight,
  CheckCircle,
  Award,
  Globe,
  Heart
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { ROUTES } from '@/shared/config/routes.config';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Privacy First',
      description: 'Your personal data is encrypted and never shared without your explicit consent.'
    },
    {
      icon: Users,
      title: 'Accessible to All',
      description: 'We believe everyone deserves access to the benefits they\'re entitled to, regardless of background.'
    },
    {
      icon: Award,
      title: 'Expert Knowledge',
      description: 'Our platform is built with input from benefits advisors and policy experts.'
    },
    {
      icon: Heart,
      title: 'Human-Centered',
      description: 'Technology that puts people first, designed to reduce stress and confusion.'
    }
  ];

  const stats = [
    { number: '50,000+', label: 'People Helped' },
    { number: '£12M+', label: 'Benefits Unlocked' },
    { number: '99.2%', label: 'Accuracy Rate' },
    { number: '24/7', label: 'AI Support' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-neutral-900 mb-6">
              Making Benefits 
              <span className="block text-primary-600">Accessible for Everyone</span>
            </h1>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Civista was born from a simple belief: everyone deserves to access the support they're entitled to. 
              We're here to break down the barriers and simplify the complex world of UK benefits.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-neutral-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-neutral-600 mb-6">
                The UK benefits system supports millions, but navigating it can be overwhelming. 
                Complex forms, changing rules, and scattered information create barriers that prevent 
                people from accessing the help they need.
              </p>
              <p className="text-lg text-neutral-600 mb-8">
                We're changing that by combining artificial intelligence with expert knowledge to 
                create a platform that's intuitive, accurate, and always available.
              </p>
              <div className="flex items-center gap-4">
                <Target className="h-8 w-8 text-primary-600" />
                <div>
                  <h3 className="font-semibold text-neutral-900">Our Goal</h3>
                  <p className="text-neutral-600">Ensure no one misses out on benefits they're entitled to</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-primary-100 to-secondary-100 rounded-2xl flex items-center justify-center">
                <Globe className="h-32 w-32 text-primary-600" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Our Values
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Everything we do is guided by these core principles
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center p-6">
                <value.icon className="h-12 w-12 text-primary-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-neutral-600">
                  {value.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Making a Real Impact
            </h2>
            <p className="text-xl text-primary-100">
              See how we're helping people across the UK
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-primary-100">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              How Civista Works
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Our intelligent platform guides you through the benefits system step by step
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                Answer Simple Questions
              </h3>
              <p className="text-neutral-600">
                Tell us about your situation through our user-friendly questionnaire
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                Get Instant Results
              </h3>
              <p className="text-neutral-600">
                Our AI analyzes your eligibility for all relevant benefits
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                Take Action
              </h3>
              <p className="text-neutral-600">
                Get guidance on how to apply and maximize your benefits
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-neutral-600 mb-8">
            Join thousands of people who have already discovered their benefits with Civista
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={ROUTES.CALCULATOR.INDEX}>
              <Button variant="primary" size="lg">
                Try Our Calculator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to={ROUTES.AUTH.REGISTER}>
              <Button variant="outline" size="lg">
                Create Free Account
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;